import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { MaterialModule } from '../../material/material.module';
import { Organisation } from '../../common/organisation';
import { MODEL_TYPE } from '../../common/linqset-model';
import { ONBOARDING_STEPS, User } from '../../common/user';
import { GET_STARTED_URL, INSIGHTS_ANALYSIS_URL, SKETCH_URL } from '../../app-urls';
import { UserService } from '../../user/user.service';
import { OnboardingService } from '../../user/onboarding.service';
import { CHECKLIST_ITEM, ChecklistItem, gettingStartedChecklist, modelToOpen, ONBOARDING_CHOICE } from '../../user/onboarding';

const TEXT:{[item in CHECKLIST_ITEM]: {title:string, description:string, action:string}} = {
    [CHECKLIST_ITEM.FIRST_MODEL]: {
        title: "Create your first model",
        description: "Use AI to build a model of one of your processes.",
        action: "Get started",
    },
    [CHECKLIST_ITEM.FIRST_CHANGE]: {
        title: "Make it yours",
        description: "Open your model and change something: rename a step, or set how long an action takes.",
        action: "Open model",
    },
    [CHECKLIST_ITEM.INSIGHTS]: {
        title: "See where the time and cost goes",
        description: "Insights show the time, cost, capacity and carbon of the work in your models.",
        action: "View insights",
    },
    [CHECKLIST_ITEM.INVITE]: {
        title: "Invite a colleague",
        description: "The people who do the work make the best reviewers. Invite someone to look at your model.",
        action: "Invite",
    },
}

// the getting started checklist on an organisation's home page. Shows itself only while there is something to do
// (see gettingStartedChecklist). Inviting is done by the home page, which knows how
@Component({
    selector: 'app-getting-started-checklist',
    templateUrl: './getting-started-checklist.html',
    styleUrls: ['./getting-started-checklist.scss'],
    standalone: true,
    imports: [MaterialModule]
})
export class GettingStartedChecklistComponent implements OnInit {
    @Input() organisation:Organisation
    @Input() users:number|undefined         // the number of users, once the home page has loaded them
    @Output() invite = new EventEmitter<void>()

    TEXT = TEXT
    ONBOARDING_CHOICE = ONBOARDING_CHOICE
    // the steps are recorded on other pages, so reading the user once is enough
    private user:User|undefined

    constructor(private userService:UserService, private onboardingService:OnboardingService, private router:Router) {}

    ngOnInit(): void {
        this.user = this.userService.getUser()
    }

    // worked out each time, so an invitation shows as soon as the organisation reloads with the new user
    get items():ChecklistItem[] {
        return gettingStartedChecklist(this.user, this.organisation, this.users)
    }

    doneCount(items:ChecklistItem[]) {
        return items.filter(i => i.done).length
    }

    // the first thing not yet done is the one to do next
    isNext(items:ChecklistItem[], item:ChecklistItem) {
        return items.find(i => !i.done) === item
    }

    act(item:CHECKLIST_ITEM) {
        const key = this.organisation._key
        switch (item) {
            case CHECKLIST_ITEM.FIRST_MODEL:
                this.router.navigateByUrl(GET_STARTED_URL(key))
                break
            case CHECKLIST_ITEM.FIRST_CHANGE: {
                const model = modelToOpen(this.organisation)
                if (model) this.router.navigateByUrl(SKETCH_URL(key, model._key, MODEL_TYPE.PROCESS))
                break
            }
            case CHECKLIST_ITEM.INSIGHTS:
                this.router.navigateByUrl(INSIGHTS_ANALYSIS_URL(key))
                break
            case CHECKLIST_ITEM.INVITE:
                this.invite.emit()
                break
        }
    }

    learn(choice:ONBOARDING_CHOICE) {
        this.onboardingService.handleChoice(choice, this.organisation._key)
    }

    async hide() {
        this.user = await this.userService.saveOnboardingStep(ONBOARDING_STEPS.CHECKLIST_HIDDEN)
    }
}
